import { useEffect, useState } from "react";
import {
  collection, query, where, onSnapshot,
  addDoc, serverTimestamp, doc, updateDoc
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUser } from "@/lib/useUser";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Plus, X, Inbox, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#1D314D";
const GOLD  = "#CEAB65";

// ── Role config ───────────────────────────────────────────────────────────────
type Role = "lender" | "buyer" | "seller" | "broker" | "consultant" | "partner" | "admin" | "staff";

const ROLE_CONTACTS: Record<Role, { id: string; nameEn: string; nameFr: string; roleEn: string; roleFr: string; avatar: string }[]> = {
  lender:     [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
    { id: "deal-coordinator",   nameEn: "Deal Coordinator",        nameFr: "Coordinateur de deal",     roleEn: "Deal Management",         roleFr: "Gestion des deals",         avatar: "DC" },
  ],
  buyer:      [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
    { id: "acquisition-advisor",nameEn: "Acquisition Advisor",     nameFr: "Conseiller en acquisition",roleEn: "Acquisition Support",     roleFr: "Support acquisition",       avatar: "AC" },
  ],
  seller:     [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
    { id: "listing-advisor",    nameEn: "Listing Advisor",         nameFr: "Conseiller en inscription",roleEn: "Listing Support",         roleFr: "Support inscription",       avatar: "LA" },
  ],
  broker:     [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
    { id: "deal-support",       nameEn: "Deal Support",            nameFr: "Support deal",             roleEn: "Deal Management",         roleFr: "Gestion des deals",         avatar: "DS" },
  ],
  consultant: [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
    { id: "client-coordinator", nameEn: "Client Coordinator",      nameFr: "Coordinateur client",      roleEn: "Client Management",       roleFr: "Gestion clients",           avatar: "CC" },
  ],
  partner:    [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
    { id: "partner-support",    nameEn: "Partner Support",         nameFr: "Support partenaire",       roleEn: "Partner Management",      roleFr: "Gestion partenaires",       avatar: "PS" },
  ],
  admin:      [
    { id: "acquizgroup-support", nameEn: "AcquizGroup Support",    nameFr: "Support AcquizGroup",      roleEn: "Technical Support",       roleFr: "Support technique",         avatar: "AS" },
  ],
  staff:      [
    { id: "acquizgroup-admin",   nameEn: "AcquizGroup Admin",      nameFr: "Admin AcquizGroup",        roleEn: "Platform Administration", roleFr: "Administration plateforme", avatar: "AA" },
  ],
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface Message {
  id: string;
  senderId: string;
  senderName?: string;
  recipientId: string;
  subject: string;
  message: string;
  read: boolean;
  createdAt: any;
}

interface Contact {
  id: string;
  nameEn: string;
  nameFr: string;
  roleEn: string;
  roleFr: string;
  avatar: string;
}

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  en: {
    title: "Messages",
    unread: (n: number) => n === 1 ? "1 unread message" : `${n} unread messages`,
    allRead: "All messages read",
    newMessage: "New Message", newContact: "New Contact",
    inbox: "Inbox", inboxDesc: "Your received messages",
    contacts: "Contacts", contactsDesc: "Your AcquizGroup team",
    noMessages: "No messages yet",
    noMessagesSub: "Messages from your AcquizGroup team will appear here",
    subject: "Subject", subjectPlaceholder: "Message subject",
    message: "Message", messagePlaceholder: "Type your message...",
    to: "To", send: "Send", cancel: "Cancel",
    sentOk: "Message sent", sentErr: "Failed to send message",
    from: "From", at: "at",
    contactId: "Contact ID (user ID)", contactName: "Display name",
    addContact: "Add Contact", reply: "Reply", writeToContact: "Write to",
  },
  fr: {
    title: "Messages",
    unread: (n: number) => n === 1 ? "1 message non lu" : `${n} messages non lus`,
    allRead: "Tous les messages lus",
    newMessage: "Nouveau message", newContact: "Nouveau contact",
    inbox: "Boîte de réception", inboxDesc: "Vos messages reçus",
    contacts: "Contacts", contactsDesc: "Votre équipe AcquizGroup",
    noMessages: "Aucun message",
    noMessagesSub: "Les messages de votre équipe AcquizGroup apparaîtront ici",
    subject: "Sujet", subjectPlaceholder: "Sujet du message",
    message: "Message", messagePlaceholder: "Rédigez votre message...",
    to: "À", send: "Envoyer", cancel: "Annuler",
    sentOk: "Message envoyé", sentErr: "Échec de l'envoi",
    from: "De", at: "à",
    contactId: "ID du contact (user ID)", contactName: "Nom affiché",
    addContact: "Ajouter le contact", reply: "Répondre", writeToContact: "Écrire à",
  },
};

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ initials, size = 36 }: { initials: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "rgba(29,49,77,0.08)", border: "1px solid rgba(29,49,77,0.12)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.33, fontWeight: 500, color: NAVY,
    }}>
      {initials}
    </div>
  );
}

// ── Compose panel ─────────────────────────────────────────────────────────────
function ComposePanel({ t, isFr, onSend, onClose, presetRecipient }: {
  t: typeof T.en; isFr: boolean;
  onSend: (recipientId: string, subject: string, message: string) => Promise<void>;
  onClose: () => void;
  presetRecipient?: Contact;
}) {
  const [recipientId, setRecipientId] = useState(presetRecipient?.id ?? "");
  const [subject, setSubject]         = useState("");
  const [message, setMessage]         = useState("");
  const [sending, setSending]         = useState(false);

  async function handleSend() {
    if (!recipientId.trim() || !subject.trim() || !message.trim()) return;
    setSending(true);
    await onSend(recipientId.trim(), subject.trim(), message.trim());
    setSending(false);
    onClose();
  }

  return (
    <Card style={{ border: `1px solid rgba(29,49,77,0.2)` }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            {t.newMessage}
          </CardTitle>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t.to}</label>
          {presetRecipient ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md text-sm"
                 style={{ background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)" }}>
              <Avatar initials={presetRecipient.avatar} size={24} />
              <span className="font-medium" style={{ color: NAVY }}>
                {isFr ? presetRecipient.nameFr : presetRecipient.nameEn}
              </span>
            </div>
          ) : (
            <Input value={recipientId} onChange={e => setRecipientId(e.target.value)}
                   placeholder={t.contactId} className="h-9 text-sm" />
          )}
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t.subject}</label>
          <Input value={subject} onChange={e => setSubject(e.target.value)}
                 placeholder={t.subjectPlaceholder} className="h-9 text-sm" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t.message}</label>
          <Textarea value={message} onChange={e => setMessage(e.target.value)}
                    placeholder={t.messagePlaceholder} rows={4} className="text-sm resize-none" />
        </div>
        <div className="flex gap-2 pt-1">
          <Button onClick={handleSend} size="sm" className="gap-2"
                  disabled={sending || !recipientId.trim() || !subject.trim() || !message.trim()}
                  style={{ background: NAVY, color: GOLD, border: `1px solid rgba(206,171,101,0.3)` }}>
            <Send className="h-3.5 w-3.5" />
            {sending ? "…" : t.send}
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>{t.cancel}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function SharedMessages({ role }: { role: Role }) {
  const { user } = useUser();
  const { language } = useLanguage();
  const isFr = language === "fr";
  const t = T[language as "en" | "fr"] ?? T.en;

  const defaultContacts = ROLE_CONTACTS[role] ?? ROLE_CONTACTS.lender;

  const [messages, setMessages]             = useState<Message[]>([]);
  const [loading, setLoading]               = useState(true);
  const [showCompose, setShowCompose]       = useState(false);
  const [composeContact, setComposeContact] = useState<Contact | undefined>();
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContactId, setNewContactId]     = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [extraContacts, setExtraContacts]   = useState<Contact[]>([]);
  const [selectedMsg, setSelectedMsg]       = useState<Message | null>(null);

  const allContacts = [...defaultContacts, ...extraContacts];

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const q = query(collection(db, "messages"), where("recipientId", "==", user.id));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() } as Message))
          .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
      );
      setLoading(false);
    }, () => { setMessages([]); setLoading(false); });
    return () => unsub();
  }, [user]);

  async function sendMessage(recipientId: string, subject: string, message: string) {
    if (!user) return;
    try {
      await addDoc(collection(db, "messages"), {
        senderId: user.id,
        senderName: user.email || role,
        recipientId, subject, message,
        read: false,
        createdAt: serverTimestamp(),
      });
      toast.success(t.sentOk);
    } catch { toast.error(t.sentErr); }
  }

  async function markRead(msg: Message) {
    if (msg.read) return;
    try { await updateDoc(doc(db, "messages", msg.id), { read: true }); } catch {}
  }

  function openMessage(msg: Message) {
    setSelectedMsg(msg);
    markRead(msg);
  }

  function addContact() {
    if (!newContactId.trim() || !newContactName.trim()) return;
    const initials = newContactName.trim().split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2);
    setExtraContacts(prev => [...prev, {
      id: newContactId.trim(),
      nameEn: newContactName.trim(), nameFr: newContactName.trim(),
      roleEn: "Contact", roleFr: "Contact", avatar: initials,
    }]);
    setNewContactId(""); setNewContactName(""); setShowAddContact(false);
  }

  function startCompose(contact?: Contact) {
    setComposeContact(contact);
    setShowCompose(true);
    setShowAddContact(false);
  }

  function fmtDate(ts: any) {
    if (!ts?.toDate) return "";
    const d = ts.toDate();
    return d.toLocaleDateString(isFr ? "fr-CA" : "en-US", { month: "short", day: "numeric" })
      + " " + t.at + " "
      + d.toLocaleTimeString(isFr ? "fr-CA" : "en-US", { hour: "2-digit", minute: "2-digit" });
  }

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-7 w-7" style={{ borderBottom: `2px solid ${NAVY}` }} />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium" style={{ color: NAVY }}>{t.title}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {unreadCount > 0 ? t.unread(unreadCount) : t.allRead}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2"
                  onClick={() => { setShowAddContact(!showAddContact); setShowCompose(false); }}>
            <Plus className="h-4 w-4" />{t.newContact}
          </Button>
          <Button size="sm" className="gap-2"
                  style={{ background: NAVY, color: GOLD, border: `1px solid rgba(206,171,101,0.3)` }}
                  onClick={() => { startCompose(undefined); }}>
            <Send className="h-4 w-4" />{t.newMessage}
          </Button>
        </div>
      </div>

      {/* Add contact */}
      {showAddContact && (
        <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Input value={newContactId} onChange={e => setNewContactId(e.target.value)}
                     placeholder={t.contactId} className="h-8 text-sm flex-1 min-w-[180px]" />
              <Input value={newContactName} onChange={e => setNewContactName(e.target.value)}
                     placeholder={t.contactName} className="h-8 text-sm flex-1 min-w-[140px]" />
              <Button size="sm" onClick={addContact}
                      disabled={!newContactId.trim() || !newContactName.trim()}
                      style={{ background: NAVY, color: "white" }}>
                {t.addContact}
              </Button>
              <button onClick={() => setShowAddContact(false)}>
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compose */}
      {showCompose && (
        <ComposePanel t={t} isFr={isFr} onSend={sendMessage}
                      onClose={() => { setShowCompose(false); setComposeContact(undefined); }}
                      presetRecipient={composeContact} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Contacts */}
        <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
              {t.contacts}
            </CardTitle>
            <CardDescription className="text-xs">{t.contactsDesc}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {allContacts.map((c, i) => (
              <div key={c.id}
                   className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                   style={{ borderTop: i === 0 ? "none" : "0.5px solid var(--color-border-tertiary)" }}>
                <Avatar initials={c.avatar} size={32} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: NAVY }}>
                    {isFr ? c.nameFr : c.nameEn}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{isFr ? c.roleFr : c.roleEn}</p>
                </div>
                <button onClick={() => startCompose(c)}
                        className="flex-shrink-0 p-1.5 rounded-md hover:bg-muted transition-colors"
                        title={`${t.writeToContact} ${isFr ? c.nameFr : c.nameEn}`}>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Inbox */}
        <div className="lg:col-span-2">
          <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                {t.inbox}
                {unreadCount > 0 && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: `${GOLD}20`, color: "#854F0B" }}>
                    {unreadCount}
                  </span>
                )}
              </CardTitle>
              <CardDescription className="text-xs">{t.inboxDesc}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {messages.length === 0 ? (
                <div className="py-14 text-center px-4">
                  <MessageSquare className="h-9 w-9 mx-auto mb-3 text-muted-foreground/25" />
                  <p className="text-sm text-muted-foreground">{t.noMessages}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.noMessagesSub}</p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={msg.id}>
                    {i > 0 && <div style={{ borderTop: "0.5px solid var(--color-border-tertiary)" }} />}
                    <div className="px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors"
                         style={{ background: selectedMsg?.id === msg.id ? "var(--color-background-secondary)" : undefined }}
                         onClick={() => openMessage(msg)}>
                      <div className="flex items-start gap-3">
                        <div className="mt-1.5 flex-shrink-0">
                          {!msg.read
                            ? <div className="w-2 h-2 rounded-full" style={{ background: GOLD }} />
                            : <div className="w-2 h-2" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${!msg.read ? "font-medium" : ""}`}
                               style={{ color: !msg.read ? NAVY : "var(--color-text-primary)" }}>
                              {msg.subject}
                            </p>
                            <span className="text-[10px] text-muted-foreground flex-shrink-0">
                              {fmtDate(msg.createdAt)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {t.from}: {msg.senderName || msg.senderId}
                          </p>
                          {selectedMsg?.id === msg.id ? (
                            <div className="mt-3 space-y-3">
                              <p className="text-sm leading-relaxed">{msg.message}</p>
                              <Button size="sm" variant="outline" className="gap-2 h-7 text-xs"
                                      onClick={e => { e.stopPropagation(); startCompose(allContacts.find(c => c.id === msg.senderId)); }}>
                                <Send className="h-3 w-3" />{t.reply}
                              </Button>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{msg.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}